import React, { Suspense, useState } from 'react';
import {
  notification,
  Tag,
  type TablePaginationConfig,
  type TableProps,
} from 'antd';
import {
  IBillingNote,
  ISearchBillingNoteReq,
  ISearchBillingNoteRes,
} from '@/interfaces/billing-note';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import { DeleteFilled } from '@ant-design/icons';
import { deleteBillingNote } from '@/adapters/billing-note';

interface IProps {
  loading: boolean;
  criteria: ISearchBillingNoteReq;
  billingNotes: ISearchBillingNoteRes;
  onDelete: (id: number) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IBillingNote> | SorterResult<IBillingNote>[],
  ) => void;
}

const _billingNoteModal: IDataModal<IBillingNote> = {
  open: false,
};

const BillingNoteTable = ({
  loading,
  criteria,
  billingNotes,
  onDelete,
  onTableChange,
}: IProps) => {
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IBillingNote>>(_billingNoteModal);

  const columns: TableProps<IBillingNote>['columns'] = [
    {
      title: 'เลขที่ใบวางบิล',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold text-blue-700',
      width: 130,
      align: 'center',
      render: (_, rec) => (
        <div className="flex flex-col ">
          <div
            onClick={() => window.open(`/billing-note/${rec.id}`, '_blank')}
            className={twMerge(
              'text-blue-700 underline cursor-pointer underline-offset-2',
              !rec?.isActive && 'text-red-500',
            )}
          >
            {_}
          </div>
        </div>
      ),
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      className: 'font-medium',
      align: 'center',
      width: 60,
    },
    {
      title: 'วันที่วางบิล',
      dataIndex: 'docDate',
      key: 'docDate',
      width: 100,
      align: 'center',
      className: 'font-medium text-black',
      render: _ => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
      render: (_, rec) => (
        <div className="truncate">{`${rec?.customerCode || ''} - ${rec?.customerName || ''}`}</div>
      ),
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'billingNoteItems',
      key: 'billingNoteItems',
      align: 'center',
      width: 110,
      render: (_, rec) => rec?.billingNoteItems?.length || 0,
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 200,
      render: (_, rec) => (
        <div className="truncate">
          {(rec?.billingNoteItems || [])
            .map(i => i.ivNo)
            .filter(Boolean)
            .join(', ')}
        </div>
      ),
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      width: 120,
      className: 'font-bold text-blue-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'ผู้สร้าง',
      dataIndex: 'createByName',
      key: 'createByName',
      align: 'center',
      width: 120,
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `green` : 'red'} className="min-w-[60px] text-center">
            {_ ? 'สำเร็จ' : 'ยกเลิก'}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'isActive',
      key: 'action',
      width: 40,
      render: (_, rec) => {
        return (
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
            {_ && (
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  const onDeleteBillingNote = async () => {
    try {
      await deleteBillingNote(Number(deleteModal?.id));
      notification.success({
        message: 'ยกเลิกใบวางบิลสำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._billingNoteModal });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IBillingNote>
        columns={columns}
        criteria={criteria}
        data={billingNotes}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1200}
        scroll
      />
      <Suspense>
        <DeleteConfirmModal
          content={`คุณต้องการยกเลิก '${deleteModal?.item?.code}' ใช่หรือไม่`}
          handleCancel={() => {
            setDeleteModal({ ..._billingNoteModal });
          }}
          handleOk={() => {
            onDeleteBillingNote();
          }}
          open={deleteModal?.open}
          title={`ใบวางบิล '${deleteModal?.item?.code}'`}
        />
      </Suspense>
    </div>
  );
};

export default BillingNoteTable;
